import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangleIcon, ArrowRightIcon, CheckIcon, FileTextIcon, InfoIcon, PhoneIcon } from 'lucide-react';
import { TreatmentHero } from '../components/treatment/TreatmentHero';
import { NotFound } from './NotFound';
import { useScrollSpy } from '../hooks/useScrollSpy';
import { treatments } from '../data/treatments';
import { complaints } from '../data/complaints';
import { clinic } from '../data/clinic';
import { corticosteroidNote, corticosteroidSideEffects } from '../data/treatmentCatalog/shared';
export function TreatmentDetail() {
  const {
    slug
  } = useParams();
  const treatment = treatments.find(t => t.slug === slug);
  const hasPrep = Boolean(treatment?.bring || treatment?.report || treatment?.preparation || treatment?.facts.noDriving);
  const hasResult = Boolean(treatment?.timeline || treatment?.result.length);
  const sections = [{
    id: 'wat-is-het',
    label: 'Wat is het?'
  }, {
    id: 'behandeling',
    label: 'De behandeling'
  }, ...(hasPrep ? [{
    id: 'voorbereiding',
    label: 'Voorbereiding'
  }] : []), ...(hasResult ? [{
    id: 'resultaat',
    label: 'Resultaat & controle'
  }] : []), ...(treatment?.complications || treatment?.sideEffects ? [{
    id: 'risicos',
    label: 'Risico’s & bijwerkingen'
  }] : [])];
  const active = useScrollSpy(sections.map(s => s.id));
  if (!treatment) return <NotFound data-id="element-759" data-mp="1bkmr0p4g7ecs" />;
  const relatedComplaints = complaints.filter(c => c.treatmentSlugs.includes(treatment.slug));
  const related = treatments.filter(t => t.category === treatment.category && t.slug !== treatment.slug).slice(0, 6);
  const hasCorticoNote = treatment.sideEffects?.includes(corticosteroidSideEffects);
  const goodToKnow = [...(treatment.preparation ?? []), ...(treatment.facts.noDriving ? ['De behandeling kan uw rijvaardigheid beïnvloeden. U mag op de dag van de behandeling geen voertuig besturen; u bent dan niet verzekerd.'] : [])];
  return <>
      <TreatmentHero treatment={treatment} data-id="element-760" data-mp="myve6bpneynv" />

      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-14 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-20 lg:px-8 lg:py-20" data-id="element-761" data-mp="mzwtisdp67be">
        <aside className="hidden lg:block" data-id="element-762" data-mp="rvlqqp28vc4j">
          <div className="sticky top-32" data-id="element-763" data-mp="vz1mzkaejagp">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted" data-id="element-764" data-mp="vy09l95wybvt">Op deze pagina</p>
            <nav aria-label="Inhoud" className="mt-4" data-id="element-765" data-mp="10s4k22ee121n">
              <ul className="border-l border-line" data-id="element-766" data-mp="15pweb32i9090">
                {sections.map(s => <li key={s.id} data-id="element-767" data-mp="1anelop0vj7ew">
                    <a href={`#${s.id}`} className={`-ml-px block border-l-2 py-2 pl-4 text-[15px] transition-colors duration-150 ${active === s.id ? 'border-primary font-medium text-primary' : 'border-transparent text-muted hover:text-ink'}`} data-id="element-768" data-mp="1amcyxnahwwhc">
                      {s.label}
                    </a>
                  </li>)}
              </ul>
            </nav>
            <div className="mt-8 rounded-2xl bg-ink p-5 text-white" data-id="element-769" data-mp="3dyax6ry2i5wf">
              <p className="font-display text-base font-semibold" data-id="element-770" data-mp="395rjluzdrarm">Vragen over deze behandeling?</p>
              <p className="mt-1 text-sm text-white/70" data-id="element-771" data-mp="346575nrdlyjv">{clinic.phoneHours}</p>
              <a href={clinic.phoneHref} className="mt-4 flex items-center gap-2 text-[15px] font-semibold text-cyan hover:text-white" data-id="element-772" data-mp="305la0k6syuxq">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-773" data-mp="p51etky5zza" />
                {clinic.phone}
              </a>
            </div>
          </div>
        </aside>

        <article className="max-w-3xl" data-id="element-774" data-mp="3nunqvptm4jr6">
          <section id="wat-is-het" className="scroll-mt-32" data-id="element-775" data-mp="3ntkx9a4wo37k">
            <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl" data-id="element-776" data-mp="3iw2ww84yewb6">Wat is het?</h2>
            <div className="mt-5 space-y-4 text-[17px] leading-relaxed text-muted" data-id="element-777" data-mp="2cga4yqif6eds">
              {treatment.intro.map((p, i) => <p key={p} className={i === 0 ? 'text-xl leading-relaxed text-ink' : undefined} data-id="element-778" data-mp="27fuwac931v25" data-mpi={i}>
                  {p}
                </p>)}
            </div>
          </section>

          <section id="behandeling" className="mt-16 scroll-mt-28" data-id="element-779" data-mp="3zpta2o7iswy">
            <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl" data-id="element-780" data-mp="3mcxe5ir6t2py">De behandeling</h2>
            <ol className="mt-6 space-y-0" data-id="element-781" data-mp="3qdr74qjhl7pp">
              {treatment.procedure.map((p, i) => <li key={p} className="relative flex gap-5 pb-8 last:pb-0" data-id="element-782" data-mp="hxxpu9hbzs7l" data-mpi={i}>
                  {i < treatment.procedure.length - 1 && <span className="absolute left-[15px] top-9 h-[calc(100%-2.25rem)] w-px bg-line" aria-hidden="true" data-id="element-783" data-mp="mx0jkxrv2c09" />}
                  <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary font-display text-sm font-semibold text-white" data-id="element-784" data-mp="dwu7ngbt9k37">
                    {i + 1}
                  </span>
                  <p className="pt-1 text-[17px] leading-relaxed text-muted" data-id="element-785" data-mp="2ynme63hnvccv">{p}</p>
                </li>)}
            </ol>
          </section>

          {hasPrep && <section id="voorbereiding" className="mt-16 scroll-mt-28" data-id="element-786" data-mp="2m4h9czeiwujv">
            <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl" data-id="element-787" data-mp="2m47kxg94mvga">Voorbereiding</h2>
            {(treatment.bring || treatment.report) && <div className="mt-6 grid gap-8 sm:grid-cols-2" data-id="element-788" data-mp="2v4vy68qp4rs4">
              {treatment.bring && <div data-id="element-789" data-mp="27etas5xq89zu">
                <h3 className="font-display text-lg font-semibold text-ink" data-id="element-790" data-mp="27gcmdj8ftwva">Wat neemt u mee?</h3>
                <ul className="mt-3 space-y-2.5" data-id="element-791" data-mp="22hszngm0sh1v">
                  {treatment.bring.map(item => <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-ink" data-id="element-792" data-mp="2h6yyh3gy4598">
                      <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-primary" aria-hidden="true" data-id="element-793" data-mp="2cdw5awg2yxtk" />
                      {item}
                    </li>)}
                </ul>
              </div>}
              {treatment.report && <div data-id="element-794" data-mp="3jsrpr71nd72f">
                <h3 className="font-display text-lg font-semibold text-ink" data-id="element-795" data-mp="3jr6qx58av1yr">Geef vooraf door</h3>
                <ul className="mt-3 space-y-2.5" data-id="element-796" data-mp="3oqb3m5ufnhtu">
                  {treatment.report.map(item => <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-ink" data-id="element-797" data-mp="3tjckgje79rg2">
                      <InfoIcon className="mt-1 h-4 w-4 shrink-0 text-primary" aria-hidden="true" data-id="element-798" data-mp="1cyyqprc5v5v" />
                      {item}
                    </li>)}
                </ul>
              </div>}
            </div>}
            {goodToKnow.length > 0 && <div className="mt-8 rounded-2xl bg-primary-soft p-6" data-id="element-799" data-mp="39talmu21n18k">
                <h3 className="font-display text-lg font-semibold text-ink" data-id="element-800" data-mp="3etokblhr8ytx">Goed om te weten</h3>
                <ul className="mt-3 space-y-3" data-id="element-801" data-mp="p4csn054xysy">
                  {goodToKnow.map(item => <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-ink/85" data-id="element-802" data-mp="u2el2bogbah3">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" data-id="element-803" data-mp="147e7jpm4rtjm" />
                      {item}
                    </li>)}
                </ul>
              </div>}
          </section>}

          {hasResult && <section id="resultaat" className="mt-16 scroll-mt-28" data-id="element-804" data-mp="qfpyxwb3bn7e">
            <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl" data-id="element-805" data-mp="qddgli1f7att">Resultaat & controle</h2>
            {treatment.timeline && <ol className="mt-6 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-3" data-id="element-806" data-mp="gh0m4hy1wsy0">
                {treatment.timeline.map(step => <li key={step.when} className="bg-white p-5" data-id="element-807" data-mp="7gvgac8ia2ly">
                    <p className="font-display text-base font-semibold text-primary" data-id="element-808" data-mp="7iewk2u5wdnq">{step.when}</p>
                    <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-809" data-mp="25vf66oy4jrn6">{step.text}</p>
                  </li>)}
              </ol>}
            <div className="mt-6 space-y-4 text-[17px] leading-relaxed text-muted" data-id="element-810" data-mp="2l4frw9azb4ae">
              {treatment.result.map(p => <p key={p} data-id="element-811" data-mp="2p46powyxakof">{p}</p>)}
            </div>
          </section>}

          {(treatment.complications || treatment.sideEffects) && <section id="risicos" className="mt-16 scroll-mt-28" data-id="element-812" data-mp="2gbmnjmjf9xyu">
              <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl" data-id="element-813" data-mp="21fnhehfvi7wc">Risico’s & bijwerkingen</h2>
              <div className="mt-6 grid gap-8 sm:grid-cols-2" data-id="element-814" data-mp="26f05f5ihdl0i">
                {treatment.complications && <div data-id="element-815" data-mp="1sfqzqzrwi9as">
                    <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-ink" data-id="element-816" data-mp="1sga75mjxdfak">
                      <AlertTriangleIcon className="h-4 w-4 text-pain" aria-hidden="true" data-id="element-817" data-mp="1xe2ardtxmpul" />
                      Mogelijke complicaties
                    </h3>
                    <ul className="mt-3 space-y-2.5" data-id="element-818" data-mp="3rs397xlklau5">
                      {treatment.complications.map(item => <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-ink" data-id="element-819" data-mp="5r5lpi5g1z2c">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-pain" aria-hidden="true" data-id="element-820" data-mp="skmnm1xhlfh" />
                          {item}
                        </li>)}
                    </ul>
                  </div>}
                {treatment.sideEffects?.map(group => <div key={group.title} data-id="element-821" data-mp="of0v6pu0wqpn">
                    <h3 className="font-display text-lg font-semibold text-ink" data-id="element-822" data-mp="jiacduqu3h0j">{group.title}</h3>
                    <ul className="mt-3 space-y-2.5" data-id="element-823" data-mp="xi1yyjvf25q5">
                      {group.items.map(item => <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-ink" data-id="element-824" data-mp="tfostucrc5su">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-muted" aria-hidden="true" data-id="element-825" data-mp="2z850q5bocf8p" />
                          {item}
                        </li>)}
                    </ul>
                  </div>)}
              </div>
              {hasCorticoNote && <p className="mt-6 text-sm text-muted" data-id="element-826" data-mp="30e78qgq7e70u">{corticosteroidNote}</p>}
              <p className="mt-6 border-t border-line pt-6 text-[15px] leading-relaxed text-muted" data-id="element-827" data-mp="2vf42isyhlura">
                Krijgt u na de behandeling klachten die u niet vertrouwt? Neem contact op met Excellent Care Clinics via{' '}
                <a href={clinic.phoneHref} className="font-medium text-primary hover:underline" data-id="element-828" data-mp="3o2zx9s4hxclf">
                  {clinic.phone}
                </a>{' '}
                of met uw huisarts. Buiten kantooruren kunt u terecht bij de huisartsenpost.
              </p>
            </section>}

          {treatment.pdf && <a href={treatment.pdf} target="_blank" rel="noreferrer" className="group mt-16 flex items-center gap-5 rounded-2xl border border-line p-5 transition-colors duration-150 hover:border-primary" data-id="element-829" data-mp="39evdektpiv12">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary" data-id="element-830" data-mp="damf5rkloedr">
                <FileTextIcon className="h-6 w-6" aria-hidden="true" data-id="element-831" data-mp="i8wd29xplt32" />
              </span>
              <span className="flex-1" data-id="element-832" data-mp="n662hpl3m4qw">
                <span className="block font-medium text-ink" data-id="element-833" data-mp="s6k9d4ayr8nc">Patiëntenfolder {treatment.name}</span>
                <span className="block text-sm text-muted" data-id="element-834" data-mp="w8p09va11r6a">Officiële folder · PDF · raadpleeg altijd de meest recente versie</span>
              </span>
              <ArrowRightIcon className="h-5 w-5 shrink-0 text-muted group-hover:text-primary" aria-hidden="true" data-id="element-835" data-mp="115xjlz6ww194" />
            </a>}

          {related.length > 0 && <section className="mt-16 border-t border-line pt-12" data-id="element-836" data-mp="1awa45req7uf9">
              <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-837" data-mp="1fwobf78i70c8">Meer {treatment.category.toLowerCase()}</h2>
              <ul className="mt-6 grid gap-x-8 sm:grid-cols-2" data-id="element-838" data-mp="1jx8a4f8q6c0h">
                {related.map(t => <li key={t.slug} className="border-b border-line" data-id="element-839" data-mp="3gxuiexttyxsz">
                    <Link to={`/behandelingen/${t.slug}`} className="group flex items-center justify-between gap-4 py-3.5" data-id="element-840" data-mp="3bzt2umvm5gm9">
                      <span className="text-[15px] font-medium text-ink group-hover:text-primary" data-id="element-841" data-mp="3pr7jwo5yn7qo">{t.name}</span>
                      <ArrowRightIcon className="h-4 w-4 shrink-0 text-muted group-hover:text-primary" aria-hidden="true" data-id="element-842" data-mp="3kzftcdmt120j" />
                    </Link>
                  </li>)}
              </ul>
            </section>}

          {relatedComplaints.length > 0 && <section className="mt-16" data-id="element-843" data-mp="ckpbwtzy8zah">
              <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-844" data-mp="cibl4xcay98q">Toegepast bij</h2>
              <ul className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-845" data-mp="8jlx8atn19au">
                {relatedComplaints.map(c => <li key={c.slug} data-id="element-846" data-mp="2edvgufi0yt5k">
                    <Link to={`/pijnbehandelingen/${c.slug}`} className="group flex items-center gap-4 rounded-2xl border border-line p-3 transition-colors duration-150 hover:border-primary" data-id="element-847" data-mp="2aap913f9ktwz">
                      <img src={c.image} alt="" className="h-16 w-20 shrink-0 rounded-xl object-cover" data-id="element-848" data-mp="249yg6v2t9mc3" />
                      <span className="flex-1 font-medium text-ink group-hover:text-primary" data-id="element-849" data-mp="1qemo6vg1i9yv">{c.title}</span>
                      <ArrowRightIcon className="mr-2 h-4 w-4 shrink-0 text-muted group-hover:text-primary" aria-hidden="true" data-id="element-850" data-mp="1gi9jl2iyibpc" />
                    </Link>
                  </li>)}
              </ul>
            </section>}
        </article>
      </div>
    </>;
}